import { z } from 'zod';

import {
  dateOnlySchema,
  moneyInputSchema,
  moneySchema,
  optionalText,
  timestampSchema,
} from '@/types/schemas';

export const INVOICE_STATUSES = ['open', 'cancelled'] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

/** Derived by the API from the amounts, so the screen never recomputes it. */
export const PAYMENT_STATES = ['unpaid', 'partly', 'paid', 'cancelled'] as const;
export type PaymentState = (typeof PAYMENT_STATES)[number];

/**
 * What `?paymentState=` may filter on: a cancelled bill is neither unpaid nor paid, so it is
 * reached with `status=cancelled`. `due` is unpaid + partly — everything still owed, the same
 * set the dashboard's pending figure counts.
 */
export const PAYMENT_STATE_FILTERS = ['unpaid', 'partly', 'paid', 'due'] as const;
export type PaymentStateFilter = (typeof PAYMENT_STATE_FILTERS)[number];

export const PAYMENT_STATE_LABEL: Record<PaymentState, string> = {
  unpaid: 'Unpaid',
  partly: 'Partly paid',
  paid: 'Paid',
  cancelled: 'Cancelled',
};

export const PAYMENT_KINDS = ['payment', 'refund'] as const;
export type PaymentKind = (typeof PAYMENT_KINDS)[number];

export const PAYMENT_METHODS = ['cash', 'upi', 'card', 'bank_transfer', 'other'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

/** What the counter takes most of the time, so every payment form starts here. */
export const DEFAULT_PAYMENT_METHOD: PaymentMethod = 'upi';

export const PAYMENT_METHOD_LABEL: Record<PaymentMethod, string> = {
  cash: 'Cash',
  upi: 'UPI',
  card: 'Card',
  bank_transfer: 'Bank transfer',
  other: 'Other',
};

export const invoiceSchema = z.object({
  id: z.number(),
  invoiceNumber: z.string(),
  memberId: z.number(),
  memberMembershipId: z.number().nullable(),
  memberPtPackageId: z.number().nullable(),
  description: z.string(),
  issueDate: dateOnlySchema,
  amount: moneySchema,
  discount: moneySchema,
  total: moneySchema,
  amountPaid: moneySchema,
  /** `total − amountPaid`, computed by the API. */
  dueAmount: moneySchema,
  status: z.enum(INVOICE_STATUSES),
  paymentState: z.enum(PAYMENT_STATES),
  notes: z.string().nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type Invoice = z.infer<typeof invoiceSchema>;

const invoiceMemberSchema = z.object({
  id: z.number(),
  memberCode: z.string(),
  firstName: z.string(),
  lastName: z.string().nullable(),
  mobile: z.string(),
});

/** A row on the Bills screen: the bill and whose it is. */
export const invoiceWithMemberSchema = invoiceSchema.extend({ member: invoiceMemberSchema });
export type InvoiceWithMember = z.infer<typeof invoiceWithMemberSchema>;

export const paymentSchema = z.object({
  id: z.number(),
  invoiceId: z.number(),
  kind: z.enum(PAYMENT_KINDS),
  amount: moneySchema,
  method: z.enum(PAYMENT_METHODS),
  paidOn: dateOnlySchema,
  reference: z.string().nullable(),
  notes: z.string().nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type Payment = z.infer<typeof paymentSchema>;

/** One bill opened on screen: its member and every payment against it. */
export const invoiceDetailSchema = invoiceWithMemberSchema.extend({
  payments: z.array(paymentSchema),
});
export type InvoiceDetail = z.infer<typeof invoiceDetailSchema>;

/**
 * `GET /invoices/collections` — money received over a range, split by how it came in. Payments
 * minus refunds by payment date, on every bill; `from`/`to` are null for an open end.
 */
export const invoiceCollectionsSchema = z.object({
  from: dateOnlySchema.nullable(),
  to: dateOnlySchema.nullable(),
  total: moneySchema,
  cash: moneySchema,
  /** UPI, card and bank transfer. */
  online: moneySchema,
  /** Recorded as "other": neither cash nor online. */
  other: moneySchema,
  byMethod: z.array(z.object({ method: z.enum(PAYMENT_METHODS), amount: moneySchema })),
});
export type InvoiceCollections = z.infer<typeof invoiceCollectionsSchema>;

/** `POST /invoices/:id/share-link` — the token behind the bill's public link. */
export const shareLinkSchema = z.object({ token: z.string().min(1) });

export const invoiceSummarySchema = z.object({
  pending: z.object({ count: z.number(), amount: moneySchema }),
  collected: z.object({ from: dateOnlySchema, to: dateOnlySchema, amount: moneySchema }),
});
export type InvoiceSummary = z.infer<typeof invoiceSummarySchema>;

// --- Forms ------------------------------------------------------------------------------

export const recordPaymentFormSchema = z.object({
  kind: z.enum(PAYMENT_KINDS),
  amount: moneyInputSchema,
  method: z.enum(PAYMENT_METHODS, { error: 'Choose how the money moved' }),
  paidOn: dateOnlySchema,
  reference: optionalText(100),
});
export type RecordPaymentFormInput = z.output<typeof recordPaymentFormSchema>;

/** The optional "taking money now" block shared by the two sell dialogs. */
export interface SalePaymentInput {
  amount: string;
  method: PaymentMethod;
  paidOn?: string;
  reference?: string;
}
